// 보스 방향 화살표(2026-10-27 근접 카메라): 플레이어가 줌을 당겨 보스가 화면 밖으로 나가면 화면 가장자리에 화살표를 띄운다.
// 근접(bossClose > 0.12)에서만 쓰고, 넓은 보스 구도는 둘이 항상 화면 안이라 켜지 않는다. DOM 요소 하나를 재사용하고
// 값(양자화)이 바뀔 때만 스타일을 쓴다. 프레임마다 객체를 만들지 않는다(스크래치 벡터 재사용).
import * as THREE from 'three/webgpu';
import { RESERVE_BOSS, type ScreenReserve } from '../ui/screenReserve';

/** 화면 가장자리 여유(NDC 절댓값). 아래는 터치 버튼·스킬 바를 피해 위쪽으로 당긴다. top은 보스 바가 없을 때의 기본 위 한계 */
export const POINTER_EDGE = { x: 0.9, top: 0.74, bottom: 0.4 } as const;
/** 아래쪽(y < CORNER_Y) 좌우 모서리는 조이스틱·스킬 버튼 자리라 화살표를 |x| ≤ CORNER_X 안으로 모은다(NDC) */
export const POINTER_CORNER = { y: -0.1, x: 0.65 } as const;
/** 켜기/끄기 경계(NDC)의 히스테리시스 폭: 경계에서 화살표가 깜빡이지 않게 켜는 선보다 이만큼 안쪽으로 들어와야 꺼진다 */
export const POINTER_HYST = 0.12;
/** 화살표 반쪽 크기(NDC, 44px 화살표 + 글자) */
const ARROW_HALF = 0.1;
/** 보스 몸 중간(y)이 화면 밖(좌우·아래)으로 이 값을 넘으면 켠다 */
const OFF_X = 0.97, OFF_BOTTOM = -0.97;

export interface PointerPlacement {
  /** 화살표를 보일지 */
  show: boolean;
  /** 가장자리 위치(NDC) */
  x: number;
  y: number;
  /** 화살표 방향(도, 0 = 위, 시계 방향 +) */
  angle: number;
}

const V = new THREE.Vector3(), W = new THREE.Vector3();

/**
 * 보스 몸 중간(월드 bx, by, bz) → 가장자리 화살표 배치. out을 채워 돌려준다(할당 없음).
 * top = 위 HUD(보스 바) 아래 경계(NDC): 보스 몸 중간이 그 위로 올라가면 몸이 바에 가린 것으로 본다. 화면 안에서 보이면 화살표를 쓰지 않는다.
 * wasShown = 직전 프레임에 켜져 있었는지(켜짐은 경계에서, 꺼짐은 POINTER_HYST만큼 안쪽에서: 깜빡임 방지).
 * 카메라 뒤쪽은 투영이 뒤집히므로 카메라 공간 좌표로 방향만 잡고 아래쪽 가장자리로 보낸다.
 */
export function placeBossPointer(cam: THREE.PerspectiveCamera, bx: number, by: number, bz: number, aspect: number, out: PointerPlacement, top: number = POINTER_EDGE.top, wasShown = false): PointerPlacement {
  cam.updateMatrixWorld();
  V.set(bx, by, bz).applyMatrix4(cam.matrixWorldInverse);
  let dx: number, dy: number;
  const topLim = Math.min(POINTER_EDGE.top, top - ARROW_HALF);
  if (V.z >= -0.2) {
    // 카메라 뒤(또는 거의 옆면): 좌우만 따르고 아래쪽으로
    dx = V.x === 0 ? 0.0001 : V.x;
    dy = -Math.max(0.5, Math.abs(V.x) * 0.15 + Math.abs(V.y));
    const n = Math.hypot(dx, dy);
    dx /= n; dy /= n;
  } else {
    V.applyMatrix4(cam.projectionMatrix);
    const h = wasShown ? POINTER_HYST : 0;
    // 켜는 선: 좌우 밖·아래 밖·보스 바 아래 경계 위. 켜진 뒤에는 그 선에서 h만큼 안쪽으로 들어와야 끈다
    // 위쪽은 몸 중간이 보스 바 아래 경계를 넘고 발(y=0)까지 그 위일 때만: 발이 화면에 보이면(큰 보스의 윗몸만 바 뒤로 잘린 경우) 보스가 보이므로 화살표로 가리지 않는다
    const midOff = V.y > top - h;
    const feetOff = midOff && W.set(bx, 0, bz).applyMatrix4(cam.matrixWorldInverse).applyMatrix4(cam.projectionMatrix).y > top - h;
    const off = Math.abs(V.x) > OFF_X - h || V.y < OFF_BOTTOM + h || feetOff;
    if (!off) { out.show = false; return out; }
    dx = V.x; dy = V.y;
  }
  // 화면 중심에서 (dx, dy) 방향 반직선이 가장자리 상자와 만나는 점
  const lim = dy >= 0 ? topLim : POINTER_EDGE.bottom;
  const k = 1 / Math.max(Math.abs(dx) / POINTER_EDGE.x, Math.abs(dy) / lim, 1e-6);
  out.x = dx * k; out.y = dy * k;
  // 아래 좌우 모서리(터치 조이스틱·스킬 버튼)를 피한다: 방향(angle)은 그대로 두고 위치만 안쪽으로
  if (out.y < POINTER_CORNER.y) out.x = Math.max(-POINTER_CORNER.x, Math.min(POINTER_CORNER.x, out.x));
  // 각도는 화면 픽셀 비율로(NDC는 가로세로가 달라 각이 틀어진다)
  out.angle = Math.atan2(dx * aspect, dy) * 180 / Math.PI;
  out.show = true;
  return out;
}

const ARROW = '<svg viewBox="0 0 48 48" width="100%" height="100%" aria-hidden="true"><path d="M24 4 L42 40 L24 31 L6 40 Z" fill="#e8402a" stroke="#fff1cf" stroke-width="3.5" stroke-linejoin="round"/></svg>';

export class BossPointer {
  private readonly root: HTMLDivElement | null;
  private readonly arrow: HTMLDivElement | null;
  private readonly spot: PointerPlacement = { show: false, x: 0, y: 0, angle: 0 };
  private shown = false;
  private lx = -99; private ly = -99; private la = -999;

  constructor(host: HTMLElement | null) {
    if (!host || typeof document === 'undefined') { this.root = null; this.arrow = null; return; }
    const root = document.createElement('div');
    root.dataset.testid = 'boss-pointer';
    root.setAttribute('aria-hidden', 'true');
    root.style.cssText = 'position:absolute;left:0;top:0;width:44px;height:44px;margin:-22px 0 0 -22px;pointer-events:none;display:none;z-index:2;filter:drop-shadow(0 2px 3px rgba(0,0,0,.7));';
    const arrow = document.createElement('div');
    arrow.style.cssText = 'width:100%;height:100%;';
    arrow.innerHTML = ARROW;
    const tag = document.createElement('div');
    tag.textContent = '보스';
    tag.style.cssText = 'position:absolute;left:50%;top:100%;transform:translateX(-50%);margin-top:-2px;font:800 12px/1 system-ui,"Malgun Gothic",sans-serif;color:#fff1cf;text-shadow:0 1px 2px #000,0 0 4px #000;white-space:nowrap;';
    root.append(arrow, tag);
    host.appendChild(root);
    this.root = root; this.arrow = arrow;
  }

  /** 매 프레임. close = 카메라 근접 정도(0..1), 보스가 없거나 가까운 시점이 아니면 숨긴다 */
  update(cam: THREE.PerspectiveCamera, active: boolean, close: number, bx: number, bodyY: number, bz: number, reserve: ScreenReserve | null = null, viewH = 0): void {
    const root = this.root;
    if (!root) return;
    // 위 한계: 보스 바(HUD 예약 영역) 아래 끝. 예약이 없으면 기본값
    let top: number = POINTER_EDGE.top;
    if (reserve && viewH > 0 && reserve.active[RESERVE_BOSS] === 1) top = Math.min(top + 0.1, Math.max(0.3, 1 - 2 * (reserve.rect[RESERVE_BOSS * 4 + 3] + 4) / viewH));
    const want = active && close > 0.12 && placeBossPointer(cam, bx, bodyY, bz, cam.aspect, this.spot, top, this.shown).show;
    if (want !== this.shown) {
      this.shown = want;
      root.style.display = want ? 'block' : 'none';
      if (!want) { this.lx = -99; this.ly = -99; this.la = -999; }
    }
    if (!want) return;
    const s = this.spot;
    const px = Math.round((s.x * 0.5 + 0.5) * 1000) / 10, py = Math.round((0.5 - s.y * 0.5) * 1000) / 10, a = Math.round(s.angle);
    if (px !== this.lx || py !== this.ly) {
      this.lx = px; this.ly = py;
      root.style.left = px + '%'; root.style.top = py + '%';
    }
    if (a !== this.la) { this.la = a; this.arrow!.style.transform = 'rotate(' + a + 'deg)'; }
  }

  dispose(): void { this.root?.remove(); }
}
